import React, { useMemo } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { FormValuesModel } from '.';
import { HearAboutTenetVariants } from 'enums/onboarding/crm';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { getPlaceholderForInputField, getPlaceholderForSelect } from '../utils';

// components
import FieldIssue from '../FieldIssue';
import SubmitButtons from '../SubmitButtons';
import FormErrorTooltip from 'components/Forms/FormErrorTooltip';
import RelationshipForm from './RelationshipForm';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import HearAboutTenetSelect from 'components/Forms/FormComponents/SelectInputs/CRM/HearAboutTenetSelect';
import {
  Button,
  Col,
  Divider,
  FormField,
  FormInput,
  Row,
  SectionIntro,
  FormInputProps,
  FormSelectProps,
  FormInputTextArea,
  FormInputTextAreaProps,
} from '@ui';

const InnerForm = () => {
  const { t } = useTranslation('onboarding');
  const { values, setFieldValue } = useFormikContext<FormValuesModel>();
  const { getActiveIssueByFieldId } = useEntityIntakeFormContext();

  const hearAboutTenetAdditionalField = useMemo(() => {
    const { hearTenet } = values;
    if (
      !hearTenet ||
      ![HearAboutTenetVariants.Referrer, HearAboutTenetVariants.Other].includes(
        hearTenet,
      )
    ) {
      return null;
    }

    switch (hearTenet) {
      case HearAboutTenetVariants.Referrer:
        return (
          <>
            <FormField<FormInputProps>
              label={
                <FieldIssue fieldId="referrerName">
                  {t('intake_form.basic_info.referrerName')}
                </FieldIssue>
              }
              name="referrerName"
              component={FormInput}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
            <FormField<FormInputProps>
              label={
                <FieldIssue fieldId="referrerCompany">
                  {t('intake_form.basic_info.referrerCompany')}
                </FieldIssue>
              }
              name="referrerCompany"
              component={FormInput}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
          </>
        );

      case HearAboutTenetVariants.Other:
        return (
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue fieldId="referrerMore">
                {t('intake_form.basic_info.referrerInformation')}
              </FieldIssue>
            }
            name="referrerInformation"
            component={FormInputTextArea}
            additionalProps={{
              rows: 5,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        );
    }
  }, [values.hearTenet]);

  const disabledParentFields = useMemo(() => {
    const referrerNameFieldIssue = getActiveIssueByFieldId('referrerName');
    const referrerCompanyFieldIssue =
      getActiveIssueByFieldId('referrerCompany');
    const referrerMoreFieldIssue = getActiveIssueByFieldId('referrerMore');

    return {
      hearAboutTenet:
        referrerNameFieldIssue?.isResolved === false ||
        referrerCompanyFieldIssue?.isResolved === false ||
        referrerMoreFieldIssue?.isResolved === false,
    };
  }, [getActiveIssueByFieldId]);

  const renderRelationships = (helpers: FieldArrayRenderProps) => {
    const canRemoveRelationships = values.relationships.length > 1;
    return (
      <Row gutter={[16, 16]}>
        {values.relationships.map((e, i) => (
          <Col key={e.id} span={24}>
            <RelationshipForm
              index={i}
              fieldName={`relationships.${i}`}
              deleteCallback={
                canRemoveRelationships ? () => helpers.remove(i) : undefined
              }
            />
          </Col>
        ))}
      </Row>
    );
  };

  const addNewRelationship = () => {
    const newRelationships = [
      ...values.relationships,
      {
        id: `custom-${generateUniqId()}`,
        template: null,
        additionalFields: [],
      },
    ];
    setFieldValue('relationships', newRelationships);
  };

  return (
    <>
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId="entityLegalName">
                {t('intake_form.basic_info.legal_name')}
              </FieldIssue>
            }
            name="legalName"
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId="entityDBA">
                {t('intake_form.basic_info.trade_name')}
              </FieldIssue>
            }
            name="tradeName"
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormSelectProps>
            label={
              <FieldIssue
                fieldId="hearTenet"
                isDisabledDueToChildComments={
                  disabledParentFields.hearAboutTenet
                }
              >
                {t('intake_form.basic_info.hearTenet')}
              </FieldIssue>
            }
            disabled={disabledParentFields.hearAboutTenet}
            name="hearTenet"
            component={HearAboutTenetSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {hearAboutTenetAdditionalField ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            {hearAboutTenetAdditionalField}
          </Col>
        ) : null}
      </Row>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        title={t('intake_form.basic_info.relationships_section_title')}
        titleVariant="h4"
      />
      <FieldArray name="relationships" render={renderRelationships} />
      <FormErrorTooltip errorKey="relationships" />
      <HideIfDisabledForm hideIfSubmitting>
        <StyledButton size="large" onClick={addNewRelationship}>
          {t('intake_form.basic_info.add_relationship_btn')}
        </StyledButton>
      </HideIfDisabledForm>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledButton = styled(Button)`
  margin-top: ${({ theme }) => theme.marginSm};
`;

export default InnerForm;
